import { useState } from 'react';
import { ApiError, type BookingResponse } from '@ota/api-client';
import { cancelPayment, createPaymentOrder, verifyPayment } from '../api';

// ── Razorpay Checkout.js global typing ───────────────────────────────────────
declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}
interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill?: { name?: string; email?: string; contact?: string };
  config?: {
    display?: { hide?: { method: string }[]; preferences?: { show_default_blocks?: boolean } };
  };
  theme?: { color?: string };
  handler: (response: RazorpaySuccessResponse) => void;
  modal?: { ondismiss?: () => void; backdropclose?: boolean };
}
interface RazorpayInstance {
  open(): void;
}
interface RazorpaySuccessResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

/** Dynamically loads the Razorpay Checkout.js script if not already present. */
function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay Checkout script'));
    document.body.appendChild(script);
  });
}

export interface RazorpayPrefill {
  name?: string;
  email?: string;
  contact?: string;
}

export interface RazorpayCheckoutCallbacks {
  /** Server-side verifyPayment succeeded. The booking is now CONFIRMED + PAID. */
  onSuccess: (booking: BookingResponse) => void;
  /**
   * Something prevented a successful capture — script load failed, order
   * creation rejected, signature mismatch, or any verify error. Message is
   * safe to render to the user.
   */
  onFailure: (message: string) => void;
  /**
   * Razorpay modal closed without payment. The backend has already been
   * told to mark the booking PAYMENT_FAILED (best-effort, fire-and-forget).
   */
  onDismiss: () => void;
}

/**
 * Shared Razorpay Checkout flow — used by both the initial CheckoutPage
 * and the "Resume payment" button on MyBookingsPage.
 *
 * <h3>Why a hook instead of a function</h3>
 * Razorpay state during checkout (loading script, awaiting modal, awaiting
 * verify) needs a visible "busy" indicator on the UI. A hook exposes that
 * cleanly without each consumer having to re-implement the spinner state.
 *
 * <h3>Idempotent against re-entry</h3>
 * The backend's {@code POST /payments/create-order/{bookingId}} returns the
 * existing CREATED Payment row if one already exists for the booking, so
 * calling {@code start()} twice for the same booking re-opens the same
 * Razorpay order rather than creating a duplicate. That's what makes the
 * "Resume payment" button safe even if a guest hit Cancel inside Razorpay,
 * waited, then came back to retry.
 *
 * <h3>Usage</h3>
 * <pre>{@code
 *   const { busy, start } = useRazorpayCheckout();
 *   await start(booking, { name, email, contact }, {
 *     onSuccess: (b) => navigate(`/my-bookings/${b.id}`),
 *     onFailure: (m) => toast(m),
 *     onDismiss: () => {},
 *   });
 * }</pre>
 */
export function useRazorpayCheckout() {
  const [busy, setBusy] = useState(false);

  const start = async (
    booking: BookingResponse,
    prefill: RazorpayPrefill,
    callbacks: RazorpayCheckoutCallbacks,
  ): Promise<void> => {
    setBusy(true);
    try {
      await loadRazorpayScript();
      // Idempotent: returns the existing CREATED Payment row when one exists.
      const order = await createPaymentOrder(booking.id);

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: booking.propertyName,
        description: `Booking ${booking.bookingCode}`,
        order_id: order.orderId,
        prefill: {
          name: prefill.name,
          email: prefill.email,
          contact: prefill.contact ?? '9999999999',
        },
        config: {
          display: {
            hide: [{ method: 'paylater' }],
            preferences: { show_default_blocks: true },
          },
        },
        theme: { color: '#1565C0' },
        handler: async (response: RazorpaySuccessResponse) => {
          try {
            const result = await verifyPayment({
              paymentId: order.paymentId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            if (result.success) {
              callbacks.onSuccess({ ...booking, status: 'CONFIRMED', paymentStatus: 'PAID' });
            } else {
              callbacks.onFailure(
                'Payment verification failed. Please contact support with booking code ' +
                  booking.bookingCode,
              );
            }
          } catch {
            callbacks.onFailure(
              'Could not verify payment. Please contact support with booking code ' +
                booking.bookingCode,
            );
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(false);
            // Best-effort: tell the backend the modal was dismissed so the
            // booking row's status reflects reality. Non-blocking — even if
            // it fails, the TTL job will sweep it up at the 15-min mark.
            cancelPayment(booking.id).catch((err) => {
              console.warn('cancelPayment call failed (non-critical):', err);
            });
            callbacks.onDismiss();
          },
        },
      });

      rzp.open();
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.status === 409) {
        callbacks.onFailure(
          'This booking is no longer active. Please make a new booking.',
        );
      } else {
        callbacks.onFailure(err instanceof Error ? err.message : 'Failed to start payment');
      }
    }
  };

  return { busy, start };
}
